<template>
	<div class="cog-spinner cog-input" :class="{'is-read-only': readonly}">
		<!-- whitespace on the following line is significant -->
		<template v-if="readonly">{{ internalValue || "&nbsp;" }}</template>
		<c-input-number
			v-else
			:id="id ? 'cog-'+id : null"
			v-model="internalValue"
			type="text"
			:placeholder="placeholder"
			:min="actualMin"
			:max="actualMax"
			:sensitivity="333"
			:step="step"
			:format="format"
			:precision="precision"
			:style="`max-width: ${maxWidth};`"
			@change="handleChange"
			@focus="handleFocus"
			@blur="handleBlur"
		/>
	</div>
</template>

<script>
	import Vue from 'vue';
	import CInputNumber from './InputNumber.ts';
	import focus from '../mixins/focus.ts';
	import { getFormatPrecision } from 'src/util/helpers';

	Vue.component('CInputNumber', CInputNumber);

	export default {
		name: 'CSpinner',
		mixins: [focus],
		inject: ['$format', '$parse'],
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			value: {
				type: String,
				default: null
			},
			id: { type: String, default: null },
			placeholder: { type: String, default: '' },
			max: { type: Number, default: Number.MAX_SAFE_INTEGER },
			min: { type: Number, default: Number.MIN_SAFE_INTEGER },
			readonly: Boolean,
			increment: { type: Number, default: null },
			format: { type: String, required: true },
			control: { type: String, default: null }
		},
		data: function () {
			return {
				internalValue: this.value
			};
		},
		computed: {
			isPercent() {
				return this.format.startsWith('P');
			},
			cleanMin() {
				return this.min === null || isNaN(this.min) || this.min < Number.MIN_SAFE_INTEGER
					? Number.MIN_SAFE_INTEGER
					: this.min;
			},
			cleanMax() {
				return this.max === null || isNaN(this.max) || this.max > Number.MAX_SAFE_INTEGER
					? Number.MAX_SAFE_INTEGER
					: this.max;
			},
			actualMin() {
				return this.cleanMin > this.cleanMax ? this.cleanMax : this.cleanMin;
			},
			actualMax() {
				return this.cleanMax < this.cleanMin ? this.cleanMin : this.cleanMax;
			},
			precision() {
				let precision = this.format ? getFormatPrecision(this.format) : 1;

				if (isNaN(precision))
					precision = 1;

				// Because percents have a true value that is a decimal, the minimum precision is 2 decimal places
				if (this.isPercent)
					return precision + 2;
				return precision;
			},
			step() {
				return Math.abs(this.increment) || (this.isPercent ? .05 : 1);
			},
			maxWidth() {
				return Math.max(Math.max(this.value.length, this.placeholder.length) * .5 + 7, 9) + 'em';
			}
		},
		watch: {
			value: function (valueProp) {
				this.internalValue = valueProp;
			},
			readonly: {
				immediate: true,
				handler: function(newValue, oldValue) {
					this.$nextTick(() => {
						if (this.value === '' && !newValue)
							this.$el.querySelector('input').removeAttribute('aria-valuenow');
					});
				}
			}
		},
		methods: {
			handleChange(newValue, previousValue) {
				this.internalValue = newValue;
				this.$emit('change', newValue, previousValue);
			},
			handleFocus(e) {
				this.addClassOnFocus();
				this.$emit('focus', e);
			},
			handleBlur(e) {
				this.removeClassOnBlur();
				this.$emit('blur', e);
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-spinner {
		// m#{i}n is not a typo but rather a sign that Sass has jumped the shark.
		// https://github.com/sass/sass/issues/2378#issuecomment-333995580
		$spinner-button-width: m#{i}n(28%, 2.5em);

		// Times New Roman is pretty light for these glyphs and seems to match other icons well.
		.el-icon-minus::before,
		.el-icon-plus::before {
			display: flex;
			align-items: center;
			justify-content: center;
			height: 100%;
			font-family: 'Times New Roman', Times, serif;
			font-size: 1.5em;
		}

		@media (-ms-high-contrast: none), (-ms-high-contrast: active) {
			// For some reason the +/- are extra big in IE
			.el-icon-minus::before,
			.el-icon-plus::before {
				font-size: .8em;
			}
		}

		.el-icon-minus::before {
			content: '\2212';
		}

		.el-icon-plus::before {
			content: '+';
		}

		.el-input-number {
			display: inline-block;
			position: relative;
			transition: max-width .1s;

			&__increase,
			&__decrease {
				position: absolute;
				bottom: var(--input__border-width);
				top: var(--input__border-width);
				z-index: $spinner-button-symbols--z;
				width: 28%;
				// stylelint-disable-next-line declaration-block-no-duplicate-properties
				width: $spinner-button-width;
				background-color: $bgColor;
				cursor: pointer;

				&.is-disabled {
					background-color: hsla(var(--background-hsl), .5);
					cursor: not-allowed;

					i::before {
						opacity: .4;
					}
				}
			}

			&__increase {
				right: var(--input__border-width);
			}

			&__decrease {
				left: var(--input__border-width);
			}
		}

		.el-input {
			display: block;

			&__inner.el-input__inner {
				padding-right: 28%;
				// stylelint-disable-next-line declaration-block-no-duplicate-properties
				padding-right: $spinner-button-width;
				padding-left: 28%;
				// stylelint-disable-next-line declaration-block-no-duplicate-properties
				padding-left: $spinner-button-width;
				text-align: center;
			}
		}
	}

	.cog-cognito--styled .el-input-number {

		&__increase,
		&__decrease {

			&:not(.is-disabled):hover {
				color: var(--highlight);

				&:not(.is-disabled) ~ .el-input .el-input__inner:not(.is-disabled) {
					border-color: var(--highlight);
				}
			}

			// spinner buttons lay on top of text input
			// see note on popper backgrounds src\style\_popper.scss
			background-image:
				linear-gradient(90deg, hsla(var(--background-hsl), .2) 0%, hsla(var(--background-hsl), .2) 100%),
				linear-gradient(90deg, var(--form__background-color) 0%, var(--form__background-color) 100%),
				linear-gradient(90deg, var(--page__background-color) 0%, var(--page__background-color) 100%),
				linear-gradient(90deg, white 0%, white 100%);
		}
	}
}
</style>
